import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSign, X } from 'lucide-react';
import { supabase } from '../admin/lib/supabaseClient';
import toast from 'react-hot-toast';
import type { OfferItemType } from '../admin/types';

interface MakeOfferButtonProps {
  itemId: string;
  itemType: OfferItemType;
  itemName: string;
  itemPrice?: number; // In cents
  allowOffers: boolean;
}

interface CustomerDetails {
  name: string;
  email: string;
  phone: string;
}

export default function MakeOfferButton({ 
  itemId, 
  itemType, 
  itemName, 
  itemPrice,
  allowOffers 
}: MakeOfferButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>({
    name: '',
    email: '',
    phone: ''
  });
  const [offerAmount, setOfferAmount] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Load saved customer details from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('customer_details');
    if (saved) {
      try {
        setCustomerDetails(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved customer details');
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!offerAmount || parseFloat(offerAmount) <= 0) {
      toast.error('Please enter a valid offer amount');
      return;
    }

    try {
      setSubmitting(true);

      // Save customer details for future use
      localStorage.setItem('customer_details', JSON.stringify(customerDetails));

      const { error } = await supabase.from('offers').insert([{
        item_type: itemType,
        item_id: itemId,
        customer_name: customerDetails.name,
        customer_email: customerDetails.email,
        customer_phone: customerDetails.phone || null,
        offer_amount: Math.round(parseFloat(offerAmount) * 100), // Convert to cents
        message: message || null,
        status: 'pending'
      }]);

      if (error) throw error;

      toast.success('Offer submitted successfully! We\'ll be in touch soon.');
      setShowModal(false);
      setOfferAmount('');
      setMessage('');
    } catch (error) {
      console.error('Error submitting offer:', error);
      toast.error('Failed to submit offer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!allowOffers) return null;

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-sm hover:shadow-md font-medium"
      >
        <DollarSign size={18} />
        Make an Offer
      </button>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto shadow-2xl"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Make an Offer</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{itemName}</p>
                  {itemPrice && (
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      Asking Price: R {(itemPrice / 100).toLocaleString()}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Customer Details */}
                <div className="border-b dark:border-gray-700 pb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Your Details</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={customerDetails.name}
                        onChange={(e) => setCustomerDetails({ ...customerDetails, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-mini-red focus:border-transparent"
                        placeholder="Your full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={customerDetails.email}
                        onChange={(e) => setCustomerDetails({ ...customerDetails, email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-mini-red focus:border-transparent"
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Phone (optional)
                      </label>
                      <input
                        type="tel"
                        value={customerDetails.phone}
                        onChange={(e) => setCustomerDetails({ ...customerDetails, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-mini-red focus:border-transparent"
                        placeholder="+27 12 345 6789"
                      />
                    </div>
                  </div>
                </div>

                {/* Offer Details */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Your Offer</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Offer Amount (ZAR) *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                          R
                        </span>
                        <input
                          type="number"
                          required
                          step="0.01"
                          min="1"
                          value={offerAmount}
                          onChange={(e) => setOfferAmount(e.target.value)}
                          className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-mini-red focus:border-transparent"
                          placeholder="Enter your offer amount"
                        />
                      </div>
                      {itemPrice && offerAmount && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {parseFloat(offerAmount) < itemPrice / 100 
                            ? `${(((itemPrice / 100 - parseFloat(offerAmount)) / (itemPrice / 100)) * 100).toFixed(1)}% below asking price`
                            : parseFloat(offerAmount) > itemPrice / 100
                            ? `${(((parseFloat(offerAmount) - itemPrice / 100) / (itemPrice / 100)) * 100).toFixed(1)}% above asking price`
                            : 'Matches asking price'
                          }
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Message (optional)
                      </label>
                      <textarea
                        rows={4}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-mini-red focus:border-transparent"
                        placeholder="Tell us why you're interested or any additional details..."
                      />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {submitting ? 'Submitting...' : 'Submit Offer'}
                  </button>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 text-center pt-2">
                  Your offer will be reviewed by our team. We'll contact you within 24-48 hours.
                </p>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
